import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus } from "lucide-react";
import { cn } from "@/utils";

interface CheckboxProps {
  label?: string;
  checked: boolean;
  className?: string;
  id?: string;
  /**
   * Tri-state. When true, renders horizontal bar (WAI-ARIA "mixed" state).
   * The `checked` prop is ignored visually while indeterminate is on.
   */
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Accessible name when no visible `label` is rendered (e.g. row checkboxes). */
  "aria-label"?: string;
}

/**
 * Checkbox — Radix checkbox primitive. 16px box, design-system tokens.
 * Replaces native <input type="checkbox"> to fix ugly browser-default styling
 * and ensure consistent cross-platform appearance.
 */
const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked,
  id,
  onChange,
  className,
  indeterminate = false,
  disabled = false,
  "aria-label": ariaLabel,
}) => {
  const radixChecked: CheckboxPrimitive.CheckedState = indeterminate
    ? "indeterminate"
    : checked;

  const handleCheckedChange = (value: CheckboxPrimitive.CheckedState) => {
    if (value === "indeterminate") return;
    onChange(value);
  };

  // The HTML spec makes the first labelable descendant of a <label> the
  // implicitly labeled control. With Radix Checkbox.Root rendering as a
  // <button>, clicking the Root inside the label fires the click once natively
  // AND once via the label's synthetic dispatch — onCheckedChange runs twice
  // and the selection toggles back off. Only wrap in <label> when there's
  // visible label text that needs the click-to-toggle association.
  const root = (
    <CheckboxPrimitive.Root
      id={id}
      checked={radixChecked}
      onCheckedChange={handleCheckedChange}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        "size-4 shrink-0 rounded-[var(--radius-sm)] border-[1.5px] border-[var(--border-strong)]",
        "bg-transparent transition-colors cursor-pointer",
        "data-[state=checked]:bg-[var(--primary)] data-[state=checked]:border-[var(--primary)]",
        "data-[state=indeterminate]:bg-[var(--primary)] data-[state=indeterminate]:border-[var(--primary)]",
        "focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus-ring)]",
        "disabled:cursor-not-allowed",
        disabled && !label && "opacity-50",
        className
      )}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-[var(--primary-foreground)]">
        {indeterminate ? (
          <Minus className="size-3" strokeWidth={3} />
        ) : (
          <Check className="size-3" strokeWidth={3} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );

  if (!label) return root;

  return (
    <label
      className={cn(
        "inline-flex items-center gap-2.5 cursor-pointer select-none",
        disabled && "cursor-not-allowed opacity-50"
      )}
      htmlFor={id}
    >
      {root}
      <span className="text-sm text-[var(--foreground)]">{label}</span>
    </label>
  );
};

export default Checkbox;
